import { get, mapValues, keys, chain, find } from "lodash"
import { createSelector } from "reselect"
import { getPortfolioEquity } from "./data"
import { getAssetClassFromMic, assetClassKeyMapIteratee } from "helpers/securities"
import { getPortfolioLmv, getPortfolioSmv } from "./market-values"
import { getForecasts } from "modules/portfolio-root/selectors/forecast"

const CONSTRAINTS = {
  tradeSwaps: constraints => {
    return !!constraints.tradeSwaps
  },
  maxTurnover: constraints => {
    const floatValue = parseFloat(constraints.maxTurnover)
    if (Number.isFinite(floatValue)) {
      return floatValue / 100
    } else {
      return 1
    }
  },
  maxLiquidationDays: constraints => {
    const floatValue = parseFloat(constraints.maxLiquidationDays)
    if (Number.isFinite(floatValue)) {
      return floatValue / 100
    } else {
      return undefined
    }
  },
  longMarketValue: (constraints, { portfolioLmv, portfolioSmv }) => {
    const netLong = parseFloat(constraints.netLong)
    let equityChange = parseFloat(constraints.equityChange)

    if (!Number.isFinite(equityChange) && !Number.isFinite(netLong)) {
      return undefined
    }

    if (Number.isFinite(equityChange)) {
      equityChange /= 100
    } else {
      equityChange = 0
    }

    let lmv = portfolioLmv
    if (Number.isFinite(netLong)) {
      lmv = (portfolioLmv + portfolioSmv) * netLong / 100
    }

    return lmv * (1 + equityChange)
  },
  shortMarketValue: (constraints, { portfolioLmv, portfolioSmv }) => {
    const netLong = parseFloat(constraints.netLong)
    let equityChange = parseFloat(constraints.equityChange)

    if (!Number.isFinite(equityChange) && !Number.isFinite(netLong)) {
      return undefined
    }

    if (Number.isFinite(equityChange)) {
      equityChange /= 100
    } else {
      equityChange = 0
    }

    let smv = portfolioSmv
    if (Number.isFinite(netLong)) {
      smv = (portfolioLmv + portfolioSmv) * (1 - netLong / 100)
    }

    return smv * (1 + equityChange)
  },

}

function getSecurityConstraints(securityConstraints, portfolioEquity) {
  if (!keys(securityConstraints).length) {
    return
  }

  return chain(securityConstraints)
    .groupBy(security => getAssetClassFromMic(security.mic))
    .mapKeys(assetClassKeyMapIteratee)
    .mapValues((securities) =>
      chain(securities)
        .map(({ ticker, mic, originalPercentEquity, constraintType, min, max }) => {
          let id, minPercentEquity, maxPercentEquity
          if (constraintType === "range") {
            minPercentEquity = originalPercentEquity + parseFloat(min) / 100
            maxPercentEquity = originalPercentEquity + parseFloat(max) / 100
          } else {
            minPercentEquity = originalPercentEquity
            maxPercentEquity = originalPercentEquity
          }

          switch (getAssetClassFromMic(mic)) {
            case "equity":
              id = { ticker, mic }
              break
            default:
              id = ticker
              break
          }

          return {
            id,
            minEconomicExposure: minPercentEquity * portfolioEquity,
            maxEconomicExposure: maxPercentEquity * portfolioEquity,
          }
        })
        .filter(position => position)
        .value(),
    )
    .value()
}

export const getCurrentConstraints = state => get(state, "modules.compareFrontier.constraints")
export const getCurrentSettings = state => get(state, "modules.compareFrontier.constraints.settings")
export const getCurrentHorizon = state => get(state, "modules.compareFrontier.constraints.settings.horizon")
export const getActivatedConstraints = state => get(state, "modules.compareFrontier.activatedConstraints")
export const getForecastConstraint = state => get(state, "modules.compareFrontier.constraints.settings.forecast")

// Check the selected forecast (from user preferences) against forecasts
export const getSelectedCustomForecast = createSelector(
  [getForecasts, getForecastConstraint],
  (forecasts, forecastConstraint) =>
    find(forecasts, ({ id }) => id === forecastConstraint)
      ? forecastConstraint
      : null,
)

export const getApiConstraints = createSelector(
  [getCurrentConstraints, getPortfolioLmv, getPortfolioSmv, getPortfolioEquity],
  (constraints, portfolioLmv, portfolioSmv, portfolioEquity) => ({
    ...mapValues(CONSTRAINTS, fn =>
      fn({ ...constraints.general }, { portfolioLmv, portfolioSmv }),
    ),
    exposure: constraints.exposure,
    securities: getSecurityConstraints(constraints.securities, portfolioEquity),
  }),
)
